import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { allReportMotives, ReportDraft, ReportMotive } from "@domain/report/report";
import { useAsync } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { RadioButton } from "@ui/components/inputs/radio";
import { Stack } from "@ui/components/layout";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { ClosableModal } from "./closableModal";

interface ReportModalProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	reportInfos: Omit<ReportDraft, "motive">;
}
export const ReportModal: React.FC<ReportModalProps> = ({ close, reportInfos, ...htmlProps }) => {
	const [chosenMotive, setChosenMotive] = useState<ReportMotive | null>(null);
	const { supportService } = useServices();
	const { loading, run } = useAsync(async () => {
		if (chosenMotive) {
			try {
				await supportService.report({ ...reportInfos, motive: chosenMotive });
				createToast(I18n.t("success"), I18n.t("report.success"), "success");
				close();
			} catch {
				createToast(I18n.t("error"), I18n.t("report.error"), "error");
			}
		}
	}, [reportInfos, chosenMotive]);

	return (
		<Container {...htmlProps} close={close} title={I18n.t("report.modal.title")} motiveChosen={!!chosenMotive}>
			<Stack gap={20}>
				<PrimaryMedium>{I18n.t("report.modal.message")}</PrimaryMedium>
				<Stack gap={6}>
					{allReportMotives.map((motive) => (
						<RadioButton
							key={motive}
							value={motive}
							id={motive}
							name="motive"
							checked={chosenMotive === motive}
							onChange={(m) => setChosenMotive(m as ReportMotive)}
							label={I18n.t(`report.motive.${motive}`)}
						/>
					))}
				</Stack>
			</Stack>
			<LoadingButton loading={loading} disabled={!chosenMotive} onClick={run}>
				{I18n.t("report.action")}
			</LoadingButton>
		</Container>
	);
};

const Container = styled(ClosableModal).withConfig({
	shouldForwardProp: (prop) => prop !== "motiveChosen",
})<{ motiveChosen: boolean }>`
	${gap(40)};

	> a {
		margin: 0 auto;
		${({ motiveChosen }) => !motiveChosen && "pointer-events: none"};
	}
`;
